import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SERVICES } from "@/data/mock";
import AuthModal from "@/components/shared/AuthModal";

const PARTY_LOGO = "/assets/party-logo.png";

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState("login");
    const navigate = useNavigate();

    const openAuth = (mode) => {
        setAuthMode(mode);
        setAuthOpen(true);
        setMobileOpen(false);
    };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
    }, [mobileOpen]);

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 transition-[background,border,box-shadow] duration-500 ${
                    scrolled ? "glass border-b border-ink/5 shadow-[0_1px_0_rgba(15,23,42,0.04)]" : "bg-white/0"
                }`}
                data-testid="site-header"
            >
                <div className="container-x flex h-[76px] items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="group flex items-center gap-3" data-testid="logo-link">
                        <div className="grid h-11 w-11 place-items-center rounded-full bg-white ring-1 ring-ink/10 overflow-hidden transition-transform duration-500 group-hover:rotate-[8deg]">
                            <img
                                src={PARTY_LOGO}
                                alt="Party logo"
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <div className="leading-tight">
                            <div className="font-display text-[15px] font-bold tracking-tight text-ink">MeetWithKTR</div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Citizen Platform</div>
                        </div>
                    </Link>

                    {/* Center menu */}
                    <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setMegaOpen(false)}>
                        {NAV_LINKS.map((item) =>
                            item.hasMega ? (
                                <button
                                    key={item.label}
                                    onMouseEnter={() => setMegaOpen(true)}
                                    onClick={() => setMegaOpen((s) => !s)}
                                    className="relative flex items-center gap-1 px-4 py-2 text-[14px] font-medium text-ink/80 transition-colors hover:text-ink"
                                    data-testid="nav-services-trigger"
                                >
                                    {item.label}
                                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                                </button>
                            ) : (
                                <NavLink
                                    key={item.label}
                                    to={item.to}
                                    onMouseEnter={() => setMegaOpen(false)}
                                    className={({ isActive }) =>
                                        `relative px-4 py-2 text-[14px] font-medium transition-colors ${
                                            isActive ? "text-ink" : "text-ink/70 hover:text-ink"
                                        }`
                                    }
                                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </NavLink>
                            ),
                        )}
                    </nav>

                    {/* Right side */}
                    <div className="hidden items-center gap-2 lg:flex">
                        <button
                            onClick={() => openAuth("login")}
                            className="px-4 py-2 text-[14px] font-medium text-ink/70 hover:text-ink"
                            data-testid="nav-login-button"
                        >
                            Login
                        </button>
                        <button
                            onClick={() => openAuth("register")}
                            className="px-4 py-2 text-[14px] font-medium text-ink/70 hover:text-ink"
                            data-testid="nav-register-button"
                        >
                            Register
                        </button>
                        <button
                            onClick={() => navigate("/request-meeting")}
                            className="btn-primary group ml-2"
                            data-testid="nav-request-meeting-cta"
                        >
                            Request a Meeting
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                    </div>

                    {/* Mobile toggle */}
                    <button
                        className="grid h-10 w-10 place-items-center rounded-full border border-ink/10 lg:hidden"
                        onClick={() => setMobileOpen((s) => !s)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>

                {/* Mega dropdown */}
                <AnimatePresence>
                    {megaOpen && (
                        <motion.div
                            onMouseEnter={() => setMegaOpen(true)}
                            onMouseLeave={() => setMegaOpen(false)}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute left-0 right-0 top-full hidden lg:block"
                            data-testid="services-mega-dropdown"
                        >
                            <div className="container-x pb-8 pt-2">
                                <div className="glass rounded-[28px] p-6 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)]">
                                    <div className="mb-5 flex items-baseline justify-between border-b border-ink/5 pb-4">
                                        <div>
                                            <div className="eyebrow">Services</div>
                                            <div className="font-display text-[22px] font-semibold tracking-tight text-ink">
                                                Six ways to engage with your government.
                                            </div>
                                        </div>
                                        <Link
                                            to="/about"
                                            className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink/60 hover:text-pink"
                                        >
                                            Learn more →
                                        </Link>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                                        {SERVICES.map((s) => {
                                            const Icon = s.icon;
                                            return (
                                                <Link
                                                    key={s.id}
                                                    to={s.to}
                                                    onClick={() => setMegaOpen(false)}
                                                    className="group flex items-start gap-4 rounded-2xl border border-ink/5 bg-slate-25 p-4 transition-colors hover:border-pink/40 hover:bg-pink/5"
                                                    data-testid={`mega-service-${s.id}`}
                                                >
                                                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-ink/10 bg-white transition-colors group-hover:border-pink group-hover:bg-white">
                                                        <Icon className="h-5 w-5 text-ink transition-colors group-hover:text-pink" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-semibold tracking-[0.2em] text-slate-400">
                                                                {s.chip}
                                                            </span>
                                                            <span className="font-display text-[15px] font-semibold text-ink">
                                                                {s.title}
                                                            </span>
                                                        </div>
                                                        <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
                                                            {s.blurb}
                                                        </p>
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Mobile menu */}
                <AnimatePresence>
                    {mobileOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 top-[76px] z-40 flex flex-col overflow-y-auto bg-white lg:hidden"
                            data-testid="mobile-menu"
                        >
                            <nav className="container-x flex flex-col gap-1 py-6">
                                {NAV_LINKS.map((item) =>
                                    item.hasMega ? (
                                        <div key={item.label} className="border-b border-ink/5 py-2">
                                            <button
                                                onClick={() => setMobileServicesOpen((s) => !s)}
                                                className="flex w-full items-center justify-between py-3 font-display text-lg font-semibold text-ink"
                                                data-testid="mobile-services-trigger"
                                            >
                                                {item.label}
                                                <ChevronDown className={`h-5 w-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                                            </button>
                                            <AnimatePresence>
                                                {mobileServicesOpen && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="flex flex-col gap-1 py-2">
                                                            {SERVICES.map((s) => (
                                                                <Link
                                                                    key={s.id}
                                                                    to={s.to}
                                                                    onClick={() => setMobileOpen(false)}
                                                                    className="flex items-center justify-between rounded-xl px-3 py-3 text-sm text-ink/80 hover:bg-slate-50"
                                                                    data-testid={`mobile-service-${s.id}`}
                                                                >
                                                                    <span>{s.title}</span>
                                                                    <ArrowUpRight className="h-4 w-4" />
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ) : (
                                        <Link
                                            key={item.label}
                                            to={item.to}
                                            onClick={() => setMobileOpen(false)}
                                            className="border-b border-ink/5 py-4 font-display text-lg font-semibold text-ink"
                                        >
                                            {item.label}
                                        </Link>
                                    ),
                                )}
                                <div className="mt-6 flex flex-col gap-3">
                                    <button
                                        onClick={() => openAuth("login")}
                                        className="btn-secondary justify-center"
                                        data-testid="mobile-login-button"
                                    >
                                        Login
                                    </button>
                                    <button
                                        onClick={() => openAuth("register")}
                                        className="btn-secondary justify-center"
                                        data-testid="mobile-register-button"
                                    >
                                        Register
                                    </button>
                                    <button
                                        onClick={() => {
                                            setMobileOpen(false);
                                            navigate("/request-meeting");
                                        }}
                                        className="btn-primary justify-center"
                                    >
                                        Request a Meeting <ArrowUpRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </nav>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            <AuthModal
                open={authOpen}
                mode={authMode}
                onSwitchMode={setAuthMode}
                onClose={() => setAuthOpen(false)}
            />
        </>
    );
}
